// src/components/deal-detail/LeadHistoryPanel.jsx
// The deal's memory of its lead days: shown only when the deal was born from
// a pipeline lead (deal.sourceLeadId). A compact needs summary plus the full
// Conversation timeline (texts, sent emails, logged activity) via the shared
// LeadConversation. Quietly renders nothing when the lead can't be found.
import { useEffect, useState } from "react";
import { ChevronDown, ChevronRight, History } from "lucide-react";
import {
  getBuyerLeadById,
  getListingLeadById,
} from "../../utils/firebasefunctions";
import LeadConversation from "../leads/LeadConversation";

const money = (v) => {
  const n = Number(String(v ?? "").replace(/[^0-9.]/g, ""));
  return n > 0 ? `$${n.toLocaleString("en-US")}` : "";
};

// Compact "what they were looking for" lines; empties skipped.
function needsLines(lead, isBuyer) {
  if (!isBuyer) {
    return [
      lead.listPrice
        ? `List ${money(lead.listPrice)}`
        : lead.estValue
          ? `Est. ${money(lead.estValue)}`
          : "",
      lead.propertyAddress || "",
    ].filter(Boolean);
  }
  const range = [money(lead.priceMin), money(lead.priceMax)]
    .filter(Boolean)
    .join(" to ");
  const beds = [
    lead.bedsMin ? `${lead.bedsMin}bd` : "",
    lead.bathsMin ? `${lead.bathsMin}ba` : "",
    lead.sqftMin ? `${Number(lead.sqftMin).toLocaleString("en-US")}+ sqft` : "",
  ]
    .filter(Boolean)
    .join(" · ");
  const preApproval =
    lead.preApprovalAmount
      ? `Pre-approved ${money(lead.preApprovalAmount)}`
      : lead.preApproval === "no"
        ? "Needs pre-approval"
        : lead.preApproval === "in_progress"
          ? "Pre-approval in progress"
          : "";
  return [
    range,
    (lead.areas || []).filter(Boolean).join(" · "),
    beds,
    (lead.propertyTypes || []).filter(Boolean).join(" · "),
    preApproval,
    lead.timeline ? `Timeline: ${lead.timeline}` : "",
    lead.financingType ? `Financing: ${lead.financingType}` : "",
    (lead.mustHaves || []).filter(Boolean).length
      ? `Must haves: ${lead.mustHaves.filter(Boolean).join(", ")}`
      : "",
    lead.motivation ? `Motivation: ${lead.motivation}` : "",
  ].filter(Boolean);
}

export default function LeadHistoryPanel({ deal }) {
  const [lead, setLead] = useState(null);
  const [open, setOpen] = useState(false);
  const isBuyer = deal?.dealtype === "Buyer";

  useEffect(() => {
    let cancelled = false;
    setLead(null);
    if (!deal?.sourceLeadId) return undefined;
    (async () => {
      const fetched = isBuyer
        ? await getBuyerLeadById(deal.sourceLeadId)
        : await getListingLeadById(deal.sourceLeadId);
      if (!cancelled) setLead(fetched);
    })();
    return () => {
      cancelled = true;
    };
  }, [deal?.sourceLeadId, isBuyer]);

  if (!deal?.sourceLeadId || !lead) return null;

  const lines = needsLines(lead, isBuyer);

  return (
    <div className="bg-white rounded-2xl border border-gray-200 shadow-sm mt-4">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center gap-2 px-5 py-4 text-left"
      >
        <History size={16} className="text-slate-500" />
        <span className="font-semibold text-slate-900">Lead History</span>
        <span className="text-xs text-slate-400">
          how this deal started, from the pipeline
        </span>
        <span className="ml-auto text-slate-400">
          {open ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
        </span>
      </button>
      {open && (
        <div className="px-5 pb-5">
          {lead.rating && (
            <span className="inline-block text-[11px] font-bold bg-amber-50 text-amber-800 border border-amber-200 rounded px-1.5 py-0.5 mb-2">
              Rated {lead.rating}
            </span>
          )}
          {lines.length > 0 && (
            <ul className="mb-4 space-y-1">
              {lines.map((l, i) => (
                <li key={i} className="text-sm text-slate-600">
                  {l}
                </li>
              ))}
            </ul>
          )}
          {/* Full story: texts, sent emails, logged activities. */}
          <LeadConversation lead={lead} />
        </div>
      )}
    </div>
  );
}
